'use client';

import { Button } from '@/components/ui/button';
import { api, useQuery } from '@/lib/convex';
import { normalizeLocale, type SupportedLocale } from '@/i18n/config';
import { Authenticated } from 'convex/react';
import { X } from 'lucide-react';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { useLocale } from 'next-intl';
import { startTransition, useEffect, useState } from 'react';
import {
  buildLowCreditsNoticeHref,
  resolveLowCreditsNoticeCta,
} from './low-credits-notice-cta';
import {
  isCriticalLowCreditStatus,
  shouldShowLowCreditsPopup,
} from './low-credits-popup-state';

const DISMISS_STORAGE_KEY = 'lowCreditsPopupDismissedAt:v2';
const DISMISS_DURATION = 12 * 60 * 60 * 1000; // 12 hours in milliseconds
const CRITICAL_DISMISS_DURATION = 10 * 60 * 1000; // 10 minutes for critical status

const LOW_CREDITS_COPY: Record<
  SupportedLocale,
  {
    title: string;
    remainingSingle: (amount: string | number) => string;
    remainingPlural: (amount: string | number) => string;
    checkUsage: string;
    subscribe: string;
    addCredits: string;
    dismissAriaLabel: string;
  }
> = {
  en: {
    title: 'Low credits',
    remainingSingle: (amount) => `You have ${amount} credit remaining`,
    remainingPlural: (amount) => `You have ${amount} credits remaining`,
    checkUsage: 'Check Usage',
    subscribe: 'Start subscription',
    addCredits: 'Add credits',
    dismissAriaLabel: 'Dismiss low credits notice',
  },
  zh: {
    title: '积分不足',
    remainingSingle: (amount) => `你还剩 ${amount} 个积分`,
    remainingPlural: (amount) => `你还剩 ${amount} 个积分`,
    checkUsage: '查看用量',
    subscribe: '开始订阅',
    addCredits: '添加积分',
    dismissAriaLabel: '关闭低积分提示',
  },
  ja: {
    title: 'クレジット残量が少なくなっています',
    remainingSingle: (amount) => `残りクレジットは ${amount} 件です`,
    remainingPlural: (amount) => `残りクレジットは ${amount} 件です`,
    checkUsage: '使用状況を確認',
    subscribe: 'サブスクリプションを開始',
    addCredits: 'クレジットを追加',
    dismissAriaLabel: 'クレジット不足のお知らせを閉じる',
  },
  ko: {
    title: '크레딧이 부족합니다',
    remainingSingle: (amount) => `남은 크레딧은 ${amount}개입니다`,
    remainingPlural: (amount) => `남은 크레딧은 ${amount}개입니다`,
    checkUsage: '사용량 확인',
    subscribe: '구독 시작',
    addCredits: '크레딧 추가',
    dismissAriaLabel: '크레딧 부족 알림 닫기',
  },
  es: {
    title: 'Créditos bajos',
    remainingSingle: (amount) => `Te queda ${amount} crédito`,
    remainingPlural: (amount) => `Te quedan ${amount} créditos`,
    checkUsage: 'Ver uso',
    subscribe: 'Iniciar suscripción',
    addCredits: 'Añadir créditos',
    dismissAriaLabel: 'Descartar aviso de créditos bajos',
  },
  'pt-BR': {
    title: 'Créditos baixos',
    remainingSingle: (amount) => `Você tem ${amount} crédito restante`,
    remainingPlural: (amount) => `Você tem ${amount} créditos restantes`,
    checkUsage: 'Ver uso',
    subscribe: 'Iniciar assinatura',
    addCredits: 'Adicionar créditos',
    dismissAriaLabel: 'Fechar aviso de créditos baixos',
  },
  fr: {
    title: 'Crédits faibles',
    remainingSingle: (amount) => `Il vous reste ${amount} crédit`,
    remainingPlural: (amount) => `Il vous reste ${amount} crédits`,
    checkUsage: 'Voir l’utilisation',
    subscribe: 'Commencer l’abonnement',
    addCredits: 'Ajouter des crédits',
    dismissAriaLabel: 'Masquer l’avis de crédits faibles',
  },
  de: {
    title: 'Wenig Guthaben',
    remainingSingle: (amount) => `Du hast noch ${amount} Guthaben`,
    remainingPlural: (amount) => `Du hast noch ${amount} Guthaben`,
    checkUsage: 'Nutzung prüfen',
    subscribe: 'Abo starten',
    addCredits: 'Guthaben hinzufügen',
    dismissAriaLabel: 'Hinweis zu niedrigem Guthaben schließen',
  },
  it: {
    title: 'Crediti in esaurimento',
    remainingSingle: (amount) => `Ti resta ${amount} credito`,
    remainingPlural: (amount) => `Ti restano ${amount} crediti`,
    checkUsage: 'Controlla utilizzo',
    subscribe: 'Inizia l’abbonamento',
    addCredits: 'Aggiungi crediti',
    dismissAriaLabel: 'Chiudi avviso crediti in esaurimento',
  },
  ru: {
    title: 'Мало кредитов',
    remainingSingle: (amount) => `У вас остался ${amount} кредит`,
    remainingPlural: (amount) => `У вас осталось ${amount} кредитов`,
    checkUsage: 'Проверить использование',
    subscribe: 'Оформить подписку',
    addCredits: 'Добавить кредиты',
    dismissAriaLabel: 'Закрыть уведомление о низком балансе кредитов',
  },
  ar: {
    title: 'الرصيد منخفض',
    remainingSingle: (amount) => `لديك ${amount} رصيد متبقٍ`,
    remainingPlural: (amount) => `لديك ${amount} أرصدة متبقية`,
    checkUsage: 'التحقق من الاستخدام',
    subscribe: 'بدء الاشتراك',
    addCredits: 'إضافة أرصدة',
    dismissAriaLabel: 'إغلاق إشعار انخفاض الرصيد',
  },
  hi: {
    title: 'क्रेडिट कम हैं',
    remainingSingle: (amount) => `आपके पास ${amount} क्रेडिट बचा है`,
    remainingPlural: (amount) => `आपके पास ${amount} क्रेडिट बचे हैं`,
    checkUsage: 'उपयोग देखें',
    subscribe: 'सदस्यता शुरू करें',
    addCredits: 'क्रेडिट जोड़ें',
    dismissAriaLabel: 'कम क्रेडिट सूचना बंद करें',
  },
  'zh-TW': {
    title: '積分不足',
    remainingSingle: (amount) => `你還剩 ${amount} 個積分`,
    remainingPlural: (amount) => `你還剩 ${amount} 個積分`,
    checkUsage: '查看用量',
    subscribe: '開始訂閱',
    addCredits: '新增點數',
    dismissAriaLabel: '關閉低積分提示',
  },
  vi: {
    title: 'Sắp hết credit',
    remainingSingle: (amount) => `Bạn còn ${amount} credit`,
    remainingPlural: (amount) => `Bạn còn ${amount} credit`,
    checkUsage: 'Xem mức dùng',
    subscribe: 'Bắt đầu đăng ký',
    addCredits: 'Thêm credit',
    dismissAriaLabel: 'Đóng thông báo credit thấp',
  },
  id: {
    title: 'Kredit hampir habis',
    remainingSingle: (amount) => `Anda memiliki ${amount} kredit tersisa`,
    remainingPlural: (amount) => `Anda memiliki ${amount} kredit tersisa`,
    checkUsage: 'Lihat penggunaan',
    subscribe: 'Mulai berlangganan',
    addCredits: 'Tambah kredit',
    dismissAriaLabel: 'Tutup pemberitahuan kredit rendah',
  },
};

export function getLocalizedLowCreditNotice(args: {
  locale: string;
  balance: number;
}): string {
  const copy = LOW_CREDITS_COPY[normalizeLocale(args.locale)];
  const amount = args.balance.toFixed(2);
  return args.balance === 1
    ? copy.remainingSingle(amount)
    : copy.remainingPlural(amount);
}

export function getLowCreditsPopupCopy(locale: string) {
  return LOW_CREDITS_COPY[normalizeLocale(locale)];
}

export function LowCreditsPopup() {
  const params = useParams();
  const orgSlug = params.orgSlug as string | undefined;
  const [isDismissed, setIsDismissed] = useState(false);
  const locale = useLocale();
  const normalizedLocale: SupportedLocale = normalizeLocale(locale);
  const copy = getLowCreditsPopupCopy(normalizedLocale);

  const { data: creditStatus } = useQuery(
    api.organizations.edge_case_handlers.getCreditStatus,
    orgSlug ? { orgSlug } : 'skip'
  );

  const { data: organization } = useQuery(
    api.organizations.queries.getBySlug,
    orgSlug ? { slug: orgSlug } : 'skip'
  );
  useEffect(() => {
    const dismissedAt = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (dismissedAt) {
      const dismissTime = parseInt(dismissedAt, 10);
      const timeSinceDismiss = Date.now() - dismissTime;

      // Use shorter duration for critical status
      const isCritical = isCriticalLowCreditStatus({
        billingPlan: organization?.billing?.plan,
        creditStatus: creditStatus?.status,
      });
      const dismissDuration = isCritical
        ? CRITICAL_DISMISS_DURATION
        : DISMISS_DURATION;

      if (timeSinceDismiss < dismissDuration) {
        startTransition(() => {
          setIsDismissed(true);
        });
      } else {
        localStorage.removeItem(DISMISS_STORAGE_KEY);
      }
    }
  }, [
    creditStatus?.status,
    creditStatus?.balance,
    organization?.billing?.plan,
  ]);

  // Reset dismissal for critical status after generations or time intervals
  useEffect(() => {
    if (!creditStatus) return;

    const isCritical = isCriticalLowCreditStatus({
      billingPlan: organization?.billing?.plan,
      creditStatus: creditStatus.status,
    });

    if (isCritical) {
      // Listen for generation events (credit usage)
      const handleGeneration = () => {
        // Reset dismissal when a generation occurs for critical status
        localStorage.removeItem(DISMISS_STORAGE_KEY);
        setIsDismissed(false);
      };

      // Listen for custom generation events
      window.addEventListener('generation-completed', handleGeneration);
      window.addEventListener('generation-started', handleGeneration);

      // Set up 10-minute timer to reset dismissal for critical status
      const timer = setInterval(() => {
        const dismissedAt = localStorage.getItem(DISMISS_STORAGE_KEY);
        if (dismissedAt) {
          localStorage.removeItem(DISMISS_STORAGE_KEY);
          setIsDismissed(false);
        }
      }, CRITICAL_DISMISS_DURATION);

      return () => {
        window.removeEventListener('generation-completed', handleGeneration);
        window.removeEventListener('generation-started', handleGeneration);
        clearInterval(timer);
      };
    }

    return undefined;
  }, [creditStatus?.status, creditStatus, organization?.billing?.plan]);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_STORAGE_KEY, Date.now().toString());
    setIsDismissed(true);
  };

  const shouldShow =
    creditStatus &&
    shouldShowLowCreditsPopup({
      billingPlan: organization?.billing?.plan,
      creditStatus: creditStatus.status,
      isDismissed,
    });

  if (!orgSlug || !shouldShow) {
    return null;
  }

  const creditText = getLocalizedLowCreditNotice({
    locale: normalizedLocale,
    balance: creditStatus.balance,
  });
  const cta = resolveLowCreditsNoticeCta({
    billingPlan: organization?.billing?.plan,
    creditStatus: creditStatus.status,
    balance: creditStatus.balance,
  });
  const primaryCtaHref = buildLowCreditsNoticeHref(orgSlug, cta);
  const primaryCtaLabel =
    cta.intent === 'subscribe'
      ? copy.subscribe
      : cta.intent === 'add-credits'
        ? copy.addCredits
        : copy.checkUsage;
  const noticeText = creditText;

  return (
    <Authenticated>
      <div className="fixed top-4 right-4 left-4 z-[100] w-auto max-w-xl sm:top-6 sm:right-6 sm:left-auto">
        <div className="bg-background/65 border-border/70 supports-[backdrop-filter]:bg-background/55 relative overflow-hidden rounded-xl border shadow-[0_18px_38px_-28px_rgba(15,23,42,0.28)] backdrop-blur-2xl">
          <div className="relative flex flex-row items-center justify-between gap-2.5 px-3 py-3 sm:px-4">
            <div className="flex min-w-0 flex-1 items-center pr-1">
              <div className="min-w-0 space-y-0.5">
                <p className="text-foreground truncate text-sm font-semibold sm:text-[15px]">
                  {copy.title}
                </p>
                <p className="text-muted-foreground truncate text-[11px] sm:text-xs">
                  {noticeText}
                </p>
              </div>
            </div>

            <div className="flex flex-shrink-0 items-center gap-1.5">
              <Button
                asChild
                variant="outline"
                size="sm"
                className="bg-background/80 border-border/70 h-8 rounded-md px-3 text-xs shadow-sm backdrop-blur-sm"
              >
                <Link href={primaryCtaHref} onClick={handleDismiss}>
                  {primaryCtaLabel}
                </Link>
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={handleDismiss}
                className="text-muted-foreground hover:text-foreground rounded-md"
                aria-label={copy.dismissAriaLabel}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Authenticated>
  );
}
